import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import onGetCurrentPath from 'utils/onGetCurrentOrgPath';

import OrgTreeView from './OrgTreeView';

import type IOrgPathField from './interfaces/IOrgPathField';

import styles from './scss/OrgPathField.module.scss';

function OrgPathField({
  name,
  orgStructureData,
  onChange,
  value,
  label,
  error,
  required,
  placeholder,
  className,
  logicalName,
  orgPathValue,
  requiresParentSelection = false,
}: IOrgPathField) {
  const [isOpen, setIsOpen] = useState(false);
  const [openIndexes, setOpenIndexes] = useState<Record<string, boolean>>({});
  const isDropdownDisabled = orgStructureData.length === 0;
  const errorId = name && error ? `${name}-error` : undefined;
  const classes = clsx(styles.root, className, {
    [styles.rootError]: error,
    [styles.disabled]: isDropdownDisabled,
  });

  const toggleOpen = (path: string) => {
    setOpenIndexes((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const handleSelect = useCallback((path: string, isTopLevel = false) => {
    setIsOpen(false);

    let newPath = logicalName;

    // Check if orgPathValue equals path and cut the last part if so
    if (orgPathValue && path === orgPathValue) {
      const pathParts = path.split('/');
      if (pathParts.length > 1) {
        // Remove the last part and join the rest
        const newPathParts = pathParts.slice(0, -1);
        newPath = `${newPathParts.join('/').trim()} / ${logicalName}`;
      } else {
        newPath = logicalName;
      }
    } else if (isTopLevel) {
      if (path) {
        newPath = `${path} / ${logicalName}`;
      } else {
        newPath = logicalName;
      }
    } else if (path) {
      // Check if orgPath ends with logicalName and remove it if it does
      const trimmedPath = path.trim();
      const trimmedLogicalName = logicalName.trim();

      if (trimmedPath.endsWith(trimmedLogicalName) && trimmedPath !== trimmedLogicalName) {
        // Calculate where to cut the path
        const cutIndex = trimmedPath.lastIndexOf(trimmedLogicalName);

        // Get the part before logicalName, and trim any trailing separators
        let basePath = trimmedPath.substring(0, cutIndex).trim();

        // Remove trailing slash and space if present
        if (basePath.endsWith('/')) {
          basePath = basePath.slice(0, -1).trim();
        }

        newPath = `${basePath} / ${trimmedLogicalName}`;
      } else if (path.startsWith(logicalName)) {
        newPath = path;
      } else {
        newPath = `${path} / ${logicalName}`;
      }
    }

    if (!path && requiresParentSelection) {
      onChange?.('');
      return;
    }

    onChange?.(newPath);
  }, [onChange, logicalName, orgPathValue, requiresParentSelection]);

  useEffect(() => {
    const currentPath = onGetCurrentPath(value).trim();
    handleSelect(currentPath);
  }, [logicalName, value, handleSelect]);

  useEffect(() => {
    if (!isDropdownDisabled) return;

    setIsOpen(false);
  }, [isDropdownDisabled]);

  const handleToggleDropdown = () => {
    if (isDropdownDisabled) return;

    setIsOpen((currentIsOpen) => !currentIsOpen);
  };

  const onClickItem = (path: string, archived: boolean, isTopLevel = false) => {
    if (archived) return;
    handleSelect(path, isTopLevel);
  };

  // We want to pass the exact current path to correctly identify which nodes
  // are children and should be disabled
  const currentSelectedPath = value || '';

  return (
    <div className={classes}>
      <span className={styles.labelText}>{`${label} ${required ? '*' : ''}`}</span>
      <div className={styles.wrapper}>
        <button
          id={name}
          name={name}
          type="button"
          className={styles.inputField}
          onClick={handleToggleDropdown}
          aria-haspopup="tree"
          aria-expanded={isOpen}
          aria-disabled={isDropdownDisabled}
          aria-describedby={errorId}
          disabled={isDropdownDisabled}
        >
          {value ? (
            <span>
              {value}
              {!logicalName && ' (New Profit Centre)'}
            </span>
          ) : (
            <span className={styles.placeholderText}>{placeholder}</span>
          )}
        </button>
        {isOpen && !isDropdownDisabled ? (
          <div className={styles.dropdown} role="tree">
            <OrgTreeView
              items={orgStructureData}
              value={value}
              openIndexes={openIndexes}
              toggleOpen={toggleOpen}
              onClickItem={onClickItem}
              currentOrgPath={currentSelectedPath}
            />
          </div>
        ) : null}
      </div>
      {error ? <span id={errorId} className={styles.errorText}>{error}</span> : null}
    </div>
  );
}

export default OrgPathField;
